import React from "react";

interface Props {
  duration?: number;  // seconds for one sweep pass, default 3.5
  delay?: number;     // seconds before first sweep, default 1.5
  angle?: number;     // gradient angle in degrees, default 25
  color?: string;     // highlight colour, default semi-transparent white
}

/**
 * Light sweep — a gloss highlight band moves diagonally across the banner
 * once every `duration` seconds. Zero assets required.
 *
 * Technique: a masked linear-gradient positioned off the left edge at 0%
 * and moved to 150% (off the right edge). The gradient has a soft-edged
 * white band in the centre so the sweep blends naturally.
 *
 * The overlay is pointer-events:none so clicks/taps pass through.
 */
const LightSweep: React.FC<Props> = ({
  duration = 3.5,
  delay = 1.5,
  angle = 25,
  // Raw rgba intentionally — this is a runtime-configurable per-banner value
  // set via effectConfig in the admin form. It cannot be a theme token because
  // different banners can have different sweep colours.
  color = "rgba(255,255,255,0.18)",
}) => {
  const animName = "oot-light-sweep";

  return (
    <>
      <style>{`
        @keyframes ${animName} {
          0%   { transform: translateX(-100%) skewX(-${angle}deg); }
          100% { transform: translateX(250%)  skewX(-${angle}deg); }
        }
      `}</style>
      {/* Full-cover transparent container — clips the band at the image edges */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {/* The sweep band itself */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "35%",
            height: "100%",
            background: `linear-gradient(
              to right,
              transparent 0%,
              ${color} 40%,
              ${color} 60%,
              transparent 100%
            )`,
            animation: `${animName} ${duration}s ease-in-out ${delay}s infinite`,
          }}
        />
      </div>
    </>
  );
};

export default LightSweep;
